import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, X, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaUrlsInput = ({ urls, onChange }) => {
  const [newUrl, setNewUrl] = useState('');

  const addUrl = () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    onChange([...urls, trimmed]);
    setNewUrl('');
  };

  const removeUrl = (index) => {
    onChange(urls.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label>Images / Videos</Label>
      <p className="text-xs text-maroon/50">Add multiple image or video URLs for the carousel</p>
      
      {urls.length > 0 && (
        <div className="space-y-2">
          {urls.map((url, index) => (
            <div key={index} className="flex items-center gap-2 bg-sand/30 border border-maroon/10 rounded-sm p-2">
              <img
                src={url}
                alt={`Media ${index + 1}`}
                className="w-12 h-12 object-cover rounded-sm border border-maroon/10 flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-sm text-maroon/70 truncate flex-1">{url}</span>
              <button
                type="button"
                onClick={() => removeUrl(index)}
                className="p-1 text-coral hover:bg-coral/10 rounded-sm flex-shrink-0"
                data-testid={`remove-media-${index}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Paste image or video URL..."
          className="input-field flex-1"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          data-testid="media-url-input"
        />
        <Button
          type="button"
          onClick={addUrl}
          className="bg-terracotta text-white hover:bg-terracotta/90 px-4"
          data-testid="add-media-url-btn"
        >
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>

      {urls.length === 0 && (
        <div className="border-2 border-dashed border-maroon/15 rounded-sm p-6 text-center">
          <Image size={32} className="mx-auto text-maroon/20 mb-2" />
          <p className="text-sm text-maroon/40">No images added yet. Add at least one image URL above.</p>
        </div>
      )}
    </div>
  );
};

const PackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    destination: '',
    duration: '',
    price: '',
    media: [],
    start_date: '',
    available_spots: '',
    highlights: '',
    included: '',
    is_featured: false
  });

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to fetch packages');
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '', description: '', destination: '', duration: '',
      price: '', media: [], start_date: '', available_spots: '',
      highlights: '', included: '', is_featured: false
    });
    setEditingPkg(null);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (pkg) => {
    const media = pkg.media && pkg.media.length > 0
      ? pkg.media
      : pkg.image_url ? [pkg.image_url] : [];

    setFormData({
      title: pkg.title || '',
      description: pkg.description || '',
      destination: pkg.destination || '',
      duration: pkg.duration || '',
      price: pkg.price?.toString() || '',
      media: media,
      start_date: pkg.start_date || '',
      available_spots: pkg.available_spots?.toString() || '',
      highlights: (pkg.highlights || []).join('\n'),
      included: (pkg.included || []).join('\n'),
      is_featured: pkg.is_featured || false
    });
    setEditingPkg(pkg);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.media.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    const token = localStorage.getItem('token');
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      available_spots: parseInt(formData.available_spots),
      image_url: formData.media[0],
      highlights: formData.highlights.split('\n').filter(h => h.trim()),
      included: formData.included.split('\n').filter(i => i.trim())
    };

    try {
      if (editingPkg) {
        await axios.put(`${API}/packages/${editingPkg.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package updated successfully');
      } else {
        await axios.post(`${API}/packages`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(editingPkg ? 'Failed to update package' : 'Failed to create package');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package deleted');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  return (
    <div data-testid="packages-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Travel Packages</h2>
        <Button onClick={openCreate} className="btn-primary" data-testid="add-package-btn">
          <Plus size={20} className="mr-2" />
          Add Package
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => {
          const imageCount = (pkg.media && pkg.media.length > 0) ? pkg.media.length : (pkg.image_url ? 1 : 0);
          return (
            <div key={pkg.id} className="card flex items-center justify-between" data-testid="package-item">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img src={pkg.image_url || (pkg.media && pkg.media[0])} alt={pkg.title} className="w-24 h-24 object-cover rounded-sm border border-maroon/10" />
                  {imageCount > 1 && (
                    <span className="absolute top-1 right-1 bg-maroon/80 text-cream text-xs px-1.5 py-0.5 rounded-sm font-bold">
                      {imageCount}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-maroon">{pkg.title}</h3>
                  <p className="text-maroon/70 text-sm">{pkg.destination} &middot; {pkg.duration} &middot; ${pkg.price}</p>
                  <p className="text-xs text-maroon/40 mt-1">{imageCount} image{imageCount !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => openEdit(pkg)}
                  variant="outline"
                  size="sm"
                  className="border-maroon/20 text-maroon hover:bg-maroon/5"
                  data-testid="edit-package-btn"
                >
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(pkg.id)}
                  variant="destructive"
                  size="sm"
                  data-testid="delete-package-btn"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">
              {editingPkg ? 'Edit Travel Package' : 'Add Travel Package'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="package-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="input-field" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination">Destination *</Label>
                <Input id="destination" value={formData.destination} onChange={(e) => setFormData({ ...formData, destination: e.target.value })} required className="input-field" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration *</Label>
                <Input id="duration" placeholder="e.g., 7 days" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} required className="input-field" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price *</Label>
                <Input id="price" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="input-field" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="available_spots">Available Spots *</Label>
                <Input id="available_spots" type="number" value={formData.available_spots} onChange={(e) => setFormData({ ...formData, available_spots: e.target.value })} required className="input-field" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date *</Label>
              <Input id="start_date" placeholder="e.g., June 15, 2026" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="input-field" />
            </div>

            <MediaUrlsInput
              urls={formData.media}
              onChange={(media) => setFormData({ ...formData, media })}
            />

            <div className="space-y-2">
              <Label htmlFor="highlights">Highlights (one per line) *</Label>
              <Textarea id="highlights" value={formData.highlights} onChange={(e) => setFormData({ ...formData, highlights: e.target.value })} placeholder="Visit ancient temples&#10;Explore rainforests&#10;Enjoy local cuisine" required rows={4} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="included">What's Included (one per line) *</Label>
              <Textarea id="included" value={formData.included} onChange={(e) => setFormData({ ...formData, included: e.target.value })} placeholder="Accommodation&#10;Meals&#10;Guided tours&#10;Transportation" required rows={4} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
            </div>

            <Button type="submit" className="btn-primary w-full">
              {editingPkg ? 'Update Package' : 'Create Package'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManager;
